import type { Take } from "../core/types";
import { idbAll, idbDelete, idbGet, idbPutRecord } from "./idb";

const STORE = "takes" as const;

export async function saveTake(take: Take): Promise<void> {
  await idbPutRecord(STORE, take);
}

export async function listTakes(): Promise<Take[]> {
  const takes = await idbAll<Take>(STORE);
  return takes.sort((a, b) => b.createdAt - a.createdAt);
}

export async function getTake(id: string): Promise<Take | undefined> {
  return idbGet<Take>(STORE, id);
}

export async function deleteTake(id: string): Promise<void> {
  await idbDelete(STORE, id);
}

/**
 * Reads a take back from disk. The handle is re-permissioned here because a
 * grant can expire between sessions; the UI asks for a click when it does.
 */
export async function takeBlob(take: Take): Promise<Blob> {
  if (!take.fileHandle) throw new Error("this take has no file handle");
  const handle = take.fileHandle;
  if (typeof handle.queryPermission === "function") {
    const state = await handle.queryPermission({ mode: "read" });
    if (state !== "granted" && typeof handle.requestPermission === "function") {
      await handle.requestPermission({ mode: "read" });
    }
  }
  const file = await handle.getFile();
  return file;
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

/** m:ss, or h:mm:ss past an hour. `round` is for durations, not clocks. */
export function formatClock(ms: number, round = false): string {
  const total = Math.max(0, Math.floor(ms / 1000) + (round ? (ms % 1000 >= 500 ? 1 : 0) : 0));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (v: number) => String(v).padStart(2, "0");
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
}

export function formatDuration(ms: number): string {
  return formatClock(ms, true);
}
